import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CheckIcon } from "../../../../assets/images/svg/twoPartyContract/success-check.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/images/svg/twoPartyContract/copy.svg";
import Amount from "../amount";
import { IFeeEstimate } from "../fees";
import styles from "../styles.module.scss";

interface ISuccessProps {
  buyerName: string;
  shareLink: string;
  amount: number;
  estimate?: IFeeEstimate;
  onViewDetails: () => void;
}

/** The published transaction: the link to send the buyer and what the seller will receive. */
const Success = ({
  buyerName,
  shareLink,
  amount,
  estimate,
  onViewDetails,
}: ISuccessProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(async () => {
    try {
      await navigator?.clipboard?.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error(error);
    }
  }, [shareLink]);

  // The system share sheet where there is one (mobile, Safari); a copy everywhere else.
  const share = useCallback(async () => {
    if (navigator?.share) {
      try {
        await navigator.share({ url: shareLink });
        return;
      } catch (error) {
        // Cancelled by the user, or refused: fall back to copying.
        if ((error as DOMException)?.name === "AbortError") return;
      }
    }
    await copyLink();
  }, [copyLink, shareLink]);

  return (
    <div className={styles.success}>
      <span className={styles.successBadge}>
        <CheckIcon />
      </span>
      <h2 className={styles.successTitle}>{t("lbl_transaction_created")}</h2>
      <p className={styles.successText}>
        {t("lbl_transaction_created_share_hint", { name: buyerName })}
      </p>

      <button
        type="button"
        className={styles.linkBox}
        onClick={copyLink}
        aria-label={t("lbl_copy_link")}>
        <span dir="ltr" className={styles.linkText}>
          {shareLink.replace(/^https?:\/\//, "")}
        </span>
        {copied ? (
          <span className={styles.copiedText}>{t("lbl_copied")}</span>
        ) : (
          <CopyIcon />
        )}
      </button>

      <div className={styles.successSummary}>
        <div className={styles.successRow}>
          <p className={styles.summaryMuted}>{t("lbl_transaction_value")}</p>
          <Amount value={amount} dark />
        </div>
        <div className={styles.successRow}>
          <p className={styles.summaryMuted}>{t("lbl_broker_role")}</p>
          <p className={styles.successValue}>{t("lbl_waffy")}</p>
        </div>
        <div className={styles.successRow}>
          <p className={styles.summaryStrong}>{t("lbl_net_you_receive")}</p>
          <Amount value={estimate?.net} highlighted />
        </div>
      </div>

      <button type="button" className={styles.primaryAction} onClick={share}>
        {copied ? t("lbl_copied") : t("lbl_share_transaction_link")}
      </button>
      <button
        type="button"
        className={styles.secondaryAction}
        onClick={onViewDetails}>
        {t("lbl_view_transaction_details")}
      </button>
    </div>
  );
};

export default Success;
